import * as React from "react";
import { ThemeProvider } from "styled-components";
import Body from "@/components/core-blocks/Body";
import CookieConsent from "@/components/core-blocks/CookieConsent";
import Footer from "@/components/core-blocks/Footer";
import Header from "@/components/core-blocks/Header";
import Main from "@/components/core-blocks/Main";
import Seo, { SEOProps } from "@/components/core-blocks/Seo";
import GlobalStyle, { MainInner, MainSection } from "@/styles/global";
import defaultTheme from "@/styles/themes/default.theme";
import S from "./news.styled";
import HeaderSubpage from "@/components/layout-blocks/HeaderSubpage";
import { IWPNews } from "@/src/types";
import HandsIcon from "@/assets/hands.svg";
import Tag from "@/components/global-components/Tag";
import { getBgColor, translateColor } from "@/src/helpers";
import FacebookIcon from "@/assets/facebook.svg";
import InstaIcon from "@/assets/instagram.svg";
import PrintIcon from "@/assets/print.svg";
import { BreadCrumbProps } from "@/components/global-components/Breadcrumbs";

export type INewsTemplate = {
  pageContext: {
    title: string;
    uri: string;
    date: string;
    modified: string;
    seo: SEOProps;
    newsInfo: IWPNews;
    breadcrumb: BreadCrumbProps;
  };
};

const NewsTemplate: React.FC<INewsTemplate> = ({
  pageContext: {
    title,
    date,
    newsInfo: { tag, contentTitle, wysiwygContent, videoUrl, ingress },
    seo,
    breadcrumb,
  },
}) => {
  const tagArray = tag.split("|");
  const tagColor = tagArray[tagArray.length - 1];
  const infoBoxColor = getBgColor("light", translateColor(tagColor));

  const handlePrint = () => {
    window.print();
  };

  const renderLinkContainer = () => {
    return (
      <>
        <S.LinkWrapper>
          <S.DetailsLarge>Publicerad: {date}</S.DetailsLarge>
        </S.LinkWrapper>
        <S.FlexWrapper>
          <S.InfoTitle>Dela</S.InfoTitle>
          <S.SoMeWrapper>
            <FacebookIcon />
            <InstaIcon />
          </S.SoMeWrapper>
        </S.FlexWrapper>
        <S.PrintButton onClick={handlePrint}>
          Skriv ut <PrintIcon />
        </S.PrintButton>
      </>
    );
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <GlobalStyle />
      <Body>
        <Header />
        <Main>
          <HeaderSubpage
            title={title}
            backgroundColor={translateColor(tagColor)}
            hasBackgroundImage={true}
            breadcrumb={breadcrumb}
          />
          <S.PrintTitle>{contentTitle}</S.PrintTitle>
          <MainSection>
            <S.Inner>
              <S.Wrapper>
                {videoUrl && (
                  <S.VideoWrapper>
                    <S.VimeoHero
                      dangerouslySetInnerHTML={{ __html: videoUrl }}
                    />
                    <S.IconWrapper>
                      <HandsIcon />
                    </S.IconWrapper>
                  </S.VideoWrapper>
                )}
                {tag && <Tag tag={tag} />}
                <S.TextContainer>
                  {title && <S.H2>{contentTitle}</S.H2>}
                  {ingress && <S.Ingress>{ingress}</S.Ingress>}
                  {wysiwygContent && (
                    <S.TextContent
                      dangerouslySetInnerHTML={{ __html: wysiwygContent }}
                    />
                  )}
                </S.TextContainer>
              </S.Wrapper>
            </S.Inner>
          </MainSection>
          <MainSection $bgColor={infoBoxColor}>
            <MainInner>
              <S.LinkBlockContainer>
                {renderLinkContainer()}
              </S.LinkBlockContainer>
            </MainInner>
          </MainSection>
          <Footer />
          <CookieConsent />
        </Main>
      </Body>
    </ThemeProvider>
  );
};

export default NewsTemplate;

// eslint-disable-next-line require-jsdoc
export function Head({ pageContext }: INewsTemplate) {
  return (
    <>
      <html lang="sv" />
      <Seo {...pageContext.seo} lang="sv" />
    </>
  );
}
